import type { CmmdBoardTask, CmmdProjectTaskAssignmentSubject } from "@cmmd-forge/contracts";

// A task carries at most one human and any number of agents. That asymmetry is
// a product rule, not a storage limit: the server's `project_task_assignments`
// unique index is keyed on the subject, so it would happily hold two humans.
// Ticking a second human therefore has to displace the first explicitly — see
// planAssignmentSelectionChange, which is where the rule is actually enforced.

export type AssignmentOperation = "replace" | "add" | "remove";

export interface AssignmentSelectionStep {
  readonly subject: CmmdProjectTaskAssignmentSubject;
  readonly operation: AssignmentOperation;
}

/**
 * The selected chips for a task, ordered human-first then agents in the task's
 * own `activeAgents` order. Deriving the order from the task rather than from
 * the roster means a roster refetch cannot reshuffle chips mid-interaction.
 */
export function assignmentSelectionValues(task: CmmdBoardTask): readonly string[] {
  const values: string[] = [];

  const humanUserId =
    task.assignment?.kind === "human_user"
      ? task.assignment.userId
      : task.assignment === null && task.assignee !== null
        ? task.assignee
        : null;
  if (humanUserId !== null) values.push(`human:${humanUserId}`);

  const primaryAgentId =
    task.assignment?.kind === "agent_member" ? task.assignment.agentMemberId : null;
  const activeAgentIds = task.activeAgents.map((agent) => agent.agentMemberId);

  const agentIds = new Set<number>(activeAgentIds);
  if (primaryAgentId !== null) agentIds.add(primaryAgentId);

  // `activeAgents` is the canonical ordering; the primary agent is prepended
  // only when the server did not also list it there.
  const ordered =
    primaryAgentId !== null && !activeAgentIds.includes(primaryAgentId)
      ? [primaryAgentId, ...activeAgentIds]
      : activeAgentIds;

  for (const id of ordered) {
    if (agentIds.delete(id)) values.push(`agent:${id}`);
  }

  return values;
}

/**
 * Turn "the user toggled this one chip" into the scoped writes that express it.
 *
 * Every step is `add` or `remove` on a single subject — never `replace`, and
 * never `unassigned`. Both of those sweep the whole task, which under
 * multi-assign would silently drop agents the user never touched.
 */
export function planAssignmentSelectionChange(
  selected: readonly string[],
  toggled: string,
): readonly AssignmentSelectionStep[] {
  const subject = parseSelectionValue(toggled);
  if (subject === null) return [];

  if (selected.includes(toggled)) {
    return [{ subject, operation: "remove" }];
  }

  if (subject.kind !== "human_user") {
    return [{ subject, operation: "add" }];
  }

  // Adding a human: displace the incumbent first so the task never briefly
  // holds two, which the board would render as an ambiguous owner cell.
  const incumbent = selected
    .map(parseSelectionValue)
    .find((candidate) => candidate?.kind === "human_user");

  return incumbent
    ? [
        { subject: incumbent, operation: "remove" },
        { subject, operation: "add" },
      ]
    : [{ subject, operation: "add" }];
}

function parseSelectionValue(value: string): CmmdProjectTaskAssignmentSubject | null {
  const [kind, rawId] = value.split(":");
  const id = Number(rawId);
  if (!Number.isFinite(id)) return null;
  if (kind === "agent") return { kind: "agent_member", agentMemberId: id };
  if (kind === "human") return { kind: "human_user", userId: id };
  return null;
}
